import React, {useMemo} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Grid,
    Heading,
    AspectRatio,
    Skeleton
} from '@salesforce/retail-react-app/app/components/shared/ui'
import DynamicImage from '@salesforce/retail-react-app/app/components/dynamic-image'
import {findImageGroupBy} from '@salesforce/retail-react-app/app/utils/image-groups-utils'
import Breadcrumb from '../../../components/breadcrumb'
import Badges from '../../../components/product-badges'
import ProductSetGrid from './product-set-grid'

const VIEW_TYPE_LARGE = 'large'

const ProductSetView = ({product, category, isLoading}) => {
    const productName = product?.name || product?.productName
    const childProducts = product?.setProducts || []

    const mainImage = useMemo(() => {
        const imageGroup = findImageGroupBy(product?.imageGroups, {
            viewType: VIEW_TYPE_LARGE,
            selectedVariationAttributes: {}
        })
        return imageGroup?.images?.[0]
    }, [product?.imageGroups])

    const mainImageSrc = mainImage?.disBaseLink || mainImage?.link

    return (
        <Box data-testid="product-set-view">
            <Skeleton isLoaded={category?.length > 0} minWidth={64}>
                <Breadcrumb categories={category} variant="pdp" isPDP mb={4} />
            </Skeleton>

            <Grid
                display="grid"
                templateColumns={{
                    base: '1fr',
                    md: '6fr 5fr',
                    xl: '5fr 5fr 0fr'
                }}
                gap={{
                    base: '10px',
                    xl: 'min(180px, 9%)'
                }}
            >
                <Box
                    position="relative"
                    w="100%"
                    maxW={{md: '560px', xl: '780px'}}
                    mb={{base: '24px', md: 0}}
                >
                    <Box position="sticky" top={0}>
                        <Box position="relative">
                            <Badges badges={product?.c_badges} />
                            <AspectRatio ratio={2 / 3}>
                                {isLoading ? (
                                    <Skeleton isLoaded={!isLoading} />
                                ) : (
                                    <DynamicImage
                                        data-testid="product-set-main-image"
                                        src={mainImageSrc}
                                        widths={['100vw', '50vw', '40vw', '40vw']}
                                        imageProps={{
                                            alt: mainImage?.alt || productName,
                                            loading: 'eager',
                                            fetchpriority: 'high',
                                            width: '100%'
                                        }}
                                    />
                                )}
                            </AspectRatio>
                        </Box>
                    </Box>
                </Box>

                <Box w="100%" pl={{base: 0, md: '14px'}}>
                    <Skeleton isLoaded={!isLoading}>
                        <Heading
                            as="h1"
                            variant="productSetHeading"
                            mb={{base: 3, md: 4}}
                            data-testid="product-set-title"
                        >
                            {productName}
                        </Heading>
                    </Skeleton>
                    <ProductSetGrid products={childProducts} isLoading={isLoading} />
                </Box>
            </Grid>
        </Box>
    )
}

ProductSetView.propTypes = {
    product: PropTypes.object,
    category: PropTypes.array,
    isLoading: PropTypes.bool
}

export default ProductSetView
